import { useRef } from 'react';

import { ChatInput } from './ChatInput';
import { PlaybookSuggestions } from './PlaybookSuggestions';

import type { ChatInputHandle } from './ChatInput';
import type { SlashCommandItem } from '../hooks/use-slash-commands';

export interface WelcomeScreenProps {
  onFirstMessage: (message: string, files?: File[]) => void;
  disabled?: boolean;
  slashCommands?: SlashCommandItem[];
  slashCommandStatus?: 'idle' | 'loading' | 'success' | 'error';
  onSlashCommandsFetch?: () => void;
  onSlashCommandsRefresh?: () => void;
}

export function WelcomeScreen({
  onFirstMessage,
  disabled,
  slashCommands,
  slashCommandStatus,
  onSlashCommandsFetch,
  onSlashCommandsRefresh,
}: WelcomeScreenProps) {
  const chatInputRef = useRef<ChatInputHandle>(null);

  return (
    <div
      data-testid="welcome-screen"
      className="sd-welcome-grid sd-welcome-ambient relative flex flex-col items-center justify-center h-full bg-sd-background overflow-hidden px-sd-4"
    >
      {/* Headline */}
      <div className="relative z-10 text-center mb-sd-6 max-w-lg">
        <h1 className="sd-reveal-1 text-[28px] font-bold text-sd-text tracking-[-0.02em] leading-tight mb-sd-2">
          Let&rsquo;s run your playbook.
        </h1>
        <p className="sd-reveal-2 text-sd-base text-sd-text-secondary leading-relaxed">
          Offload your SOPs. Monitor disputes. Automate reports.
          <br />
          <span className="text-sd-text-muted">shiftagent keeps your payments operation running.</span>
        </p>
      </div>

      {/* Input */}
      <div className="relative z-10 w-full max-w-lg sd-reveal-3">
        <ChatInput
          ref={chatInputRef}
          onSend={onFirstMessage}
          disabled={disabled}
          placeholder="Define a playbook, set up a monitor, pull a report..."
          slashCommands={slashCommands}
          slashCommandStatus={slashCommandStatus}
          onSlashCommandsFetch={onSlashCommandsFetch}
          onSlashCommandsRefresh={onSlashCommandsRefresh}
        />
      </div>

      {/* Prompt suggestions */}
      <PlaybookSuggestions
        className="relative z-10 w-full max-w-lg mt-sd-5"
        onSelect={(text) => {
          chatInputRef.current?.setValue(text);
          chatInputRef.current?.focus();
        }}
      />
    </div>
  );
}
